import { i18nFn, MilestonesDTO, useTheme } from '@springbig/shared';
import { Box, HStack, Pressable, Progress, VStack } from 'native-base';
import React, { useEffect, useState } from 'react';
import { Caption } from '~/shared/Typography';
import { faTrophy, faGift, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useAppNavigate } from '~/pages';
import { SpIcon } from '~/shared/Icon';
import { Card } from '~/shared/Theme';

type MilestonesProps = {
    data: MilestonesDTO;
    t: i18nFn;
};

export const Milestones = ({ data, t }: MilestonesProps) => {

    const { navigate, params } = useAppNavigate();

    const isSpendMilestones = () => {

        return (data?.spend_milestones?.highest_spend_milestone);
    };

    const isPointsMilestones = () => {

        return (data?.points_milestones?.highest_points_milestone);
    };

    const milestone = () => {

        if (isSpendMilestones()) {
            return data?.spend_milestones?.milestones
                ?.filter((m) => m.achieved)
                ?.sort((a, b) => b.value - a.value)?.[0];
        }

        return data?.points_milestones?.milestones
            ?.filter((m) => m.achieved)
            ?.sort((a, b) => b.value - a.value)?.[0];
    };

    const allMilestonesAchieved = () => {

        if (isSpendMilestones()) {
            const allSpendAchieved = data?.spend_milestones?.milestones.every(m => m.achieved);
            return allSpendAchieved;
        }
        else if (isPointsMilestones()) {
            const allPointsAchieved = data?.points_milestones?.milestones.every(m => m.achieved);
            return allPointsAchieved;
        }

        return false;
    };

    const noMilestonesAchieved = () => {

        if (isSpendMilestones()) {
            const noSpendAchieved = data?.spend_milestones?.milestones.every(m => !m.achieved);
            return noSpendAchieved;
        }
        else if (isPointsMilestones()) {
            const noPointsAchieved = data?.points_milestones?.milestones.every(m => !m.achieved);
            return noPointsAchieved;
        }

        return false;
    };

    const progressValue = () => {

        let milestones;
        let lifetimeAchieved;

        if (isSpendMilestones()) {
            milestones = data.spend_milestones.milestones;
            lifetimeAchieved = data.spend_milestones.lifetime_achieved;
        }
        else if (isPointsMilestones()) {
            milestones = data.points_milestones.milestones;
            lifetimeAchieved = data.points_milestones.lifetime_achieved;
        }

        const nextMilestone = milestones?.find(m => !m.achieved);

        if (!nextMilestone) {
            return 100;
        }

        const progress = Math.min((lifetimeAchieved / nextMilestone.value) * 100, 100);

        return Math.round(progress);
    };

    const [rewards, setRewards] = useState([]);

    useEffect(() => {
        setRewards(milestone()?.rewards);
    },[data]);

    const [currentRewardIndex, setCurrentRewardIndex] = useState(0);

    useEffect(() => {

        if (rewards && rewards?.length > 1) {
            const interval = setInterval(() => {

                setCurrentRewardIndex((prevIndex) => (prevIndex + 1) % rewards.length);
            }, 5000);

            return () => clearInterval(interval);
        }
    }, [rewards]);

    const {
        primary,
    } = useTheme();

    return (
        <Card hasShadow>
            <HStack justifyContent="space-between" alignItems="center">
                <Box height="full" width="1" bg="gray" mr={2} />

                <VStack flex={1}>
                    <Caption fontSize="sm">{t('homepage.milestones.achieved')}</Caption>
                    <HStack alignItems="center">
                        <SpIcon icon={faTrophy} size={16} color={primary} />
                        <Caption fontSize="14" fontWeight="bold" ml={1}>
                            {noMilestonesAchieved() ? 'Loyalty Member' : milestone()?.name ?? 'none'}
                        </Caption>
                    </HStack>
                </VStack>

                {!noMilestonesAchieved() && rewards?.length > 0 && <Box height="full" width="1" bg="gray" mx={2} />}

                {!noMilestonesAchieved() && rewards?.length > 0 && (
                    <VStack flex={1}>
                        <Caption fontSize="sm" color="gray.500">
                            {t('homepage.milestones.benefitsInclude')} {noMilestonesAchieved()}
                        </Caption>
                        <HStack alignItems="center">
                            <SpIcon icon={faGift} size={16} color={primary} />
                            <Caption fontSize="14" fontWeight="bold" ml={1}>
                                {rewards[currentRewardIndex]?.item_name}
                            </Caption>
                        </HStack>
                    </VStack>
                )}

                <Pressable onPress={() => navigate(`w/${params.wallet_id}/milestones`)}>
                    <SpIcon icon={faInfoCircle} size={16} color="gray" />
                </Pressable>
            </HStack>

            <Progress
                value={progressValue()}
                size="sm"
                mt={3}
                borderRadius="full"
                bg="gray"
                _filledTrack={{ bg: 'green' }}
            />

            {isSpendMilestones() && data?.spend_milestones?.closest_milestone && (
                <Caption mt={3} textAlign="center">
                    {t('homepage.milestones.spendMore', [
                        data?.spend_milestones?.milestones.find(e => e.id === data?.spend_milestones?.closest_milestone)
                            ?.remaining
                    ])}
                </Caption>
            )}

            {allMilestonesAchieved() && (
                <Caption fontSize="sm" fontStyle="italic" textAlign="center" mt={2}>
                    {t('homepage.milestones.allAchieved')}
                </Caption>
            )}
        </Card>
    );
};
